import { useEffect, useRef, useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { VoltApiError } from "../voltApi";
import { errorText } from "../workshop/workshopApi";
import { fundingListApi } from "./fundingListApi";
import { num, parseAmount, proposedAmount } from "./fundingText";
import type { FundingChange, FundingLogEntry, FundingRow } from "./types";

export const CONFLICT_TEXT =
  "Jemand anderes hat diesen Antrag gerade geändert. Die Liste wird neu geladen, bitte noch einmal eintragen.";

/** On the list, amount, comment and history of one application; only for rows with a proposal (version set). */
export const FundingEdit = ({
  row,
  onSaved,
  onReload,
}: {
  row: FundingRow & { version: number };
  onSaved: (id: number, update: (r: FundingRow) => FundingRow) => void;
  onReload: () => Promise<void>;
}) => {
  const [amountText, setAmountText] = useState(num(row.betrag));
  const [comment, setComment] = useState(row.kommentar ?? "");
  const [included, setIncluded] = useState(row.aufnehmen);
  const [message, setMessage] = useState<string | null>(null);
  const [log, setLog] = useState<FundingLogEntry[] | null>(null);

  // Last saved state; queued saves read it instead of a possibly stale prop.
  const saved = useRef(row);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const generation = useRef(0);

  // Keep each input in step when the list is reloaded or saved elsewhere.
  useEffect(() => {
    saved.current = row;
  }, [row]);
  useEffect(() => setAmountText(num(row.betrag)), [row.betrag]);
  useEffect(() => setComment(row.kommentar ?? ""), [row.kommentar]);
  useEffect(() => setIncluded(row.aufnehmen), [row.aufnehmen]);

  const loadLog = async () => {
    try {
      setLog(await fundingListApi.log(row.antragId));
    } catch {
      setLog([]);
    }
  };
  useEffect(() => {
    void loadLog();
    // The history reloads after each save, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.antragId]);

  const run = async (change: (r: FundingRow) => FundingChange | null) => {
    const current = saved.current;
    const body = change(current);
    if (!body) return;
    setMessage(null);
    try {
      const r = await fundingListApi.change(
        current.antragId,
        current.version,
        body,
      );
      const next = {
        ...current,
        version: r.version,
        aufnehmen: r.aufnehmen,
        betrag: r.betrag,
        kommentar: r.kommentar,
        geaendertVon: r.geaendertVon ?? current.geaendertVon,
        geaendertAm: r.geaendertAm ?? current.geaendertAm,
      };
      saved.current = next;
      onSaved(current.antragId, (old) => ({ ...old, ...next }));
      void loadLog();
    } catch (e) {
      // Changes queued behind a failed one were made on the old state: drop them.
      generation.current++;
      setAmountText(num(current.betrag));
      setComment(current.kommentar ?? "");
      setIncluded(current.aufnehmen);
      if (e instanceof VoltApiError && e.status === 409) {
        setMessage(CONFLICT_TEXT);
        await onReload();
      } else {
        setMessage(errorText(e));
      }
    }
  };

  /** Saves one after another, each with the version the previous one returned. */
  const save = (change: (r: FundingRow) => FundingChange | null) => {
    const gen = generation.current;
    queue.current = queue.current.then(() =>
      gen === generation.current ? run(change) : undefined,
    );
  };

  const toggle = (on: boolean) => {
    setIncluded(on);
    save((r) => {
      if (r.aufnehmen === on) return null;
      const change: FundingChange = { aufnehmen: on };
      if (on && r.betrag === 0) change.betrag = proposedAmount(r);
      return change;
    });
  };

  const saveAmount = () => {
    const n = parseAmount(amountText);
    if (n === null) {
      setMessage(
        "Betrag nicht lesbar, bitte als Zahl eingeben, zum Beispiel 12.500,00",
      );
      setAmountText(num(saved.current.betrag));
      return;
    }
    save((r) => (n !== r.betrag ? { betrag: n } : null));
  };

  const saveComment = () => {
    const next = comment.trim() || null;
    save((r) => (next !== (r.kommentar ?? null) ? { kommentar: next } : null));
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={included}
          onCheckedChange={(c) => toggle(c === true)}
        />
        auf die Liste
      </label>
      <div className="grid gap-1.5">
        <Label htmlFor="qf-amount">Betrag in Euro</Label>
        <Input
          id="qf-amount"
          value={amountText}
          onChange={(e) => setAmountText(e.target.value)}
          onBlur={saveAmount}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          inputMode="decimal"
          className="text-right tabular-nums"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="qf-comment">Kommentar der Fraktion</Label>
        <Textarea
          id="qf-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={saveComment}
          maxLength={2000}
        />
      </div>
      {message ? (
        <p role="alert" className="text-sm text-destructive">
          {message}
        </p>
      ) : null}
      {row.geaendertVon && row.geaendertVon !== "import" ? (
        <p className="text-xs text-muted-foreground">
          Zuletzt geändert von {row.geaendertVon}
          {row.geaendertAm
            ? `, ${new Date(row.geaendertAm).toLocaleString("de-DE")}`
            : ""}
        </p>
      ) : null}
      <div className="text-xs text-muted-foreground">
        <p className="font-medium">Verlauf</p>
        {log === null ? <p>lädt …</p> : null}
        {log?.length === 0 ? <p>noch keine Änderung der Fraktion</p> : null}
        <ul className="flex flex-col gap-1">
          {log?.map((l) => (
            <li key={l.version}>
              {new Date(l.geaendert_am).toLocaleString("de-DE")} ·{" "}
              {l.geaendert_von_name ?? "unbekannt"}:{" "}
              {l.aufnehmen_alt !== l.aufnehmen_neu &&
                `Liste ${l.aufnehmen_neu ? "ja" : "nein"}; `}
              {l.betrag_alt !== l.betrag_neu &&
                `Betrag ${num(Number(l.betrag_alt))} → ${num(Number(l.betrag_neu))}; `}
              {l.kommentar_alt !== l.kommentar_neu && "Kommentar geändert"}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
